import { formatDistance } from "date-fns";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Icon, Popup, Table, Label } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import {
  convertFileTypeToIcon,
  convertFullName,
  convertPartiesAbleToShare,
  convertRoleToSharingDisplay,
  convertSharingWithToColor,
  dateFormatDeadlineWithTime,
  partyIsAgent,
  partyIsOtherAgent,
  truncateText,
  hasOtherSideClients,
  docIsTypePdf,
  docIsTypeLink,
} from "../../../../app/common/util/util";
import DocActionButtons from "../../docComponents/DocActionButtons";
import {
  addHistoryToDb,
  // updateDocAddSharingInDb,
  updateDocInDb,
  updateDocRemoveSharingInDb,
  // updateTransAddSharingInDb,
} from "../../../../app/firestore/firestoreService";
import {
  handleDocSharingUpdate,
  handleDocumentEmailSending,
  // sendOfferAlertEmailToAdmin,
  // sendOfferAlertEmailToBrokerageStaff,
} from "../../docComponents/docSharingUtils";
import _ from "lodash";
import { openModal } from "../../../../app/common/modals/modalSlice";
import { functionShareWithAgent } from "../../../../app/firestore/functionsService";
import { serverTimestamp } from "firebase/firestore";
import { toast } from "react-toastify";
// import { useMediaQuery } from "react-responsive";

export default function DocTransActiveListItem({ doc }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { transaction, allParties } = useSelector((state) => state.transaction);
  const { currentUserProfile } = useSelector((state) => state.profile);
  const { emailTemplates } = useSelector((state) => state.emailTemplates);
  // const isMobile = useMediaQuery({ query: "(max-width:768px)" });

  // Check if document has unresolved comments
  const unresolvedCommentCount = doc.unresolvedCommentCount || 0;
  const hasUnresolvedComments = unresolvedCommentCount > 0;

  // Handle clicking on the comment icon to view document with comments
  function handleCommentClick() {
    if (docIsTypePdf(doc)) {
      navigate(`/transactions/${doc.transactionId}/documents/${doc.id}/view`, {
        state: { showComments: true }
      });
    }
  }

  // Function to get signature status for other side clients
  function getOtherSideClientSignatureStatus(party) {
    if (!partyIsOtherAgent(transaction, party) || !doc.signingDetails) {
      return [];
    }

    // Determine which roles are the other side clients based on this agent's representation
    let otherSideClientRoles = [];
    if (party.role === "Listing Agent") {
      // Listing agent's clients are Sellers
      otherSideClientRoles = ["Seller", "Seller 2", "Seller 3"];
    } else if (party.role === "Buyer Agent") {
      // Buyer agent's clients are Buyers
      otherSideClientRoles = ["Buyer", "Buyer 2", "Buyer 3"];
    }

    // Get signature status for each client role that exists in signingDetails
    const clientStatuses = [];
    otherSideClientRoles.forEach((role) => {
      const roleKey = role.toLowerCase().replace(" ", ""); // Convert "Seller 2" to "seller2"
      const signingDetail =
        doc.signingDetails[roleKey] || doc.signingDetails[role];

      if (signingDetail && signingDetail.sentOut) {
        const clientName =
          signingDetail.firstName && signingDetail.lastName
            ? `${signingDetail.firstName} ${signingDetail.lastName}`
            : role;
        const status = signingDetail.signed
          ? "signed"
          : "waiting for signature";
        clientStatuses.push(`${role}: ${status} (${clientName})`);
      }
    });

    return clientStatuses;
  }

  async function handleSharingClick(party) {
    const partyRoleCamel = _.camelCase(party.role);
    if (doc.doNotShareWithRole?.includes(party.role)) {
      toast.warning("This document should not be shared with this party.");
      return;
    }
    const agentProfile = transaction.agentProfile?.lastName
      ? transaction.agentProfile
      : currentUserProfile;
    const sharedByName = agentProfile.firstName + " " + agentProfile.lastName;
    if (doc.sharingWithRole?.[partyRoleCamel]) {
      try {
        updateDocRemoveSharingInDb(doc.id, party, transaction, allParties, (doc.sharedLinkedDocId ? doc.sharedLinkedDocId : null));
        addHistoryToDb(
          transaction.id,
          currentUserProfile,
          "unshared",
          doc.name,
          party
        );
      } catch (error) {
        throw error;
      }
    } else if (     // not sharing with role
      partyIsAgent(party.role) &&
      party.isUser &&
      !party.isLinked &&
      !party.declinedLinking
    ) {
      dispatch(
        openModal({
          modalType: "DocShareAgentLinking",
          modalProps: { doc: doc, party: party, transaction: transaction },
        })
      );
    } else {
      // Check if sharing with a non-user agent and no other-side clients exist
      if (
        partyIsAgent(party.role) &&
        !party.isUser &&
        !hasOtherSideClients(allParties, transaction.agentRepresents) &&
        !transaction.hideAgentNoClientsPrompt
      ) {
        dispatch(
          openModal({
            modalType: "DocShareAgentNoClientsPrompt",
            modalProps: { doc: doc, party: party, transaction: transaction },
          })
        );
        return;
      }
      try {
        // Temporarily disable client restoration to debug basic sharing  // TODO LIZ
        // const restoreAgentClients = false; // partyIsAgent(party.role);
        handleDocSharingUpdate(
          doc,
          party,
          allParties,
          transaction,
          currentUserProfile,
          dispatch
        );
        // updateDocAddSharingInDb(
        //   doc.id,
        //   party,
        //   transaction,
        //   allParties,
        //   restoreAgentClients
        // );
        // updateTransAddSharingInDb(transaction.id, party, transaction);
        // addHistoryToDb(
        //   transaction.id,
        //   currentUserProfile,
        //   "shared",
        //   doc.name,
        //   party
        // );
      } catch (error) {
        throw error;
      }
      
      if (partyIsAgent(party.role) && party.isUser && party.isLinked) {
        updateDocInDb(
          doc.id,
          {
            sentToAgent: true,
            sentToAgentId: party.id,
            sentDocIsDirty: false,
            sentToAgentAt: serverTimestamp(),
          },
          true
        );
        let resultFromSharing = await functionShareWithAgent({
          doc: doc,
          party: party,
          sharedBy: sharedByName,
          sharedStatus: true,
          sharedStatusAt: serverTimestamp(),
        });

        const sharedLinkedDocId = resultFromSharing;

        updateDocInDb(
          doc.id,
          {
            sharedLinkedDocId: sharedLinkedDocId,
          },
          true
        );
      } else {
        console.log("DEBUG: NOT calling functionShareWithAgent - conditions not met");
      }
      // Use shared function to handle email sending with custom email check
      handleDocumentEmailSending(
        party,
        doc,
        transaction,
        currentUserProfile,
        dispatch,
        emailTemplates
      );
    }
    // if (
    //   partyIsAgent(party.role) &&
    //   doc?.isOffer &&
    //   transaction?.agentProfile?.managerDetails
    //     ?.sendEmailNotificationsOfOffersSentToOtherAgent
    // ) {
    //   sendOfferAlertEmailToBrokerageStaff(
    //     party,
    //     doc,
    //     currentUserProfile,
    //     transaction
    //   );
    // }
  }

  return (
    <Table.Row key={doc.id}>
      <Table.Cell>
        <Icon
          name={
            docIsTypeLink(doc)
              ? "linkify"
              : `file ${convertFileTypeToIcon(doc)} outline`
          }
          size="large"
        />
        &nbsp;
        {truncateText(doc.name, 60)}
        {hasUnresolvedComments && (
          <Popup
            trigger={
              <Icon
                name="comment alternate"
                color="orange"
                style={{
                  // backgroundColor: "white",
                  // borderRadius: "50%",
                  // // padding: "2px",
                  // border: "1px solid orange",
                  // boxShadow: "0 1px 1px rgba(0,0,0,0.2)",
                  marginLeft: "8px",
                  cursor: "pointer"
                }}
                onClick={handleCommentClick}
              />
            }
            content={`${unresolvedCommentCount} unresolved comment${unresolvedCommentCount > 1 ? 's' : ''}`}
            position="top center"
            size="small"
          />
        )}
      </Table.Cell>
      <Table.Cell textAlign="center">
        {doc.signingComplete && (
          <Icon color="green" name="check" size="large" />
        )}
      </Table.Cell>

      {doc.shared && <Table.Cell>{doc.sharedBy}</Table.Cell>}
      <Table.Cell>
        {convertPartiesAbleToShare(allParties, transaction.agentRepresents).map(
          (party, index) => (
            <React.Fragment key={index}>
              {party.email && (
                <Popup
                  trigger={
                    <Label
                      key={party.role}
                      style={{ cursor: "pointer", marginBottom: "2px" }}
                      color={convertSharingWithToColor(doc, party)}
                      onClick={() => handleSharingClick(party)}
                    >
                      {convertRoleToSharingDisplay(party.role)}
                      {party.hasPoa && " POA"}
                    </Label>
                  }
                >
                  <p className="tiny bottom margin">
                    <span className="bold text blue">{party.role}:</span>{" "}
                    {convertFullName(party)}
                    {party.hasPoa && party.poaFullName && ` (POA: ${party.poaFullName})`}
                    <br />
                    {party.email}
                  </p>
                  {partyIsAgent(party.role) &&
                    party.hasTransactionCoordinator &&
                    party.transactionCoordinator?.firstName &&
                    party.transactionCoordinator?.lastName &&
                    party.transactionCoordinator?.email && (
                      <p className="tiny bottom margin">
                        <span className="bold text blue">TC:</span>{" "}
                        {convertFullName(party.transactionCoordinator)}
                      </p>
                    )}
                  {partyIsAgent(party.role) &&
                    party.isUser &&
                    party.isLinked &&
                    doc.sentToAgentAt && (
                      <p>
                        <span className="bold text blue">Sent out</span>{" "}
                        {dateFormatDeadlineWithTime(doc.sentToAgentAt)}
                      </p>
                    )}
                  {(() => {
                    const clientStatuses =
                      getOtherSideClientSignatureStatus(party);
                    return (
                      clientStatuses.length > 0 && (
                        <div>
                          <p className="tiny bottom margin">
                            <span className="bold text blue">
                              Client Status:
                            </span>
                          </p>
                          {clientStatuses.map((status, index) => (
                            <p
                              key={index}
                              className="tiny bottom margin"
                              style={{ marginLeft: "10px" }}
                            >
                              {status}
                            </p>
                          ))}
                        </div>
                      )
                    );
                  })()}
                </Popup>
              )}
            </React.Fragment>
          )
        )}
      </Table.Cell>
      <Table.Cell>
        <p>
          {!doc.sharedFromAnotherAgent &&
          doc.updatedAt &&
          doc.updatedAt instanceof Date
            ? formatDistance(doc.updatedAt, new Date(), { addSuffix: true })
            : ""}
          {doc.sharedFromAnotherAgent &&
          (doc.sharedStatus === true || doc.sharedStatus === null)
            ? "Shared " +
              (doc.sharedStatusAt instanceof Date
                ? doc.sharedStatusAt.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    timeZone: doc.timeZone || "America/Denver",
                  })
                : doc.sharedFromAnotherAgent &&
                  doc.sentToAgentAt instanceof Date
                ? doc.sentToAgentAt.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    timeZone: doc.timeZone || "America/Denver",
                  })
                : "")
            : ""}
          {doc.sharedFromAnotherAgent && doc.sharedStatus === false
            ? "Unshared " +
              (doc.sharedFromAnotherAgent && doc.sharedStatusAt instanceof Date
                ? doc.sharedStatusAt.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    timeZone: doc.timeZone || "America/Denver",
                  })
                : "")
            : ""}
        </p>
      </Table.Cell>
      <Table.Cell>
        <DocActionButtons doc={doc} />
      </Table.Cell>
    </Table.Row>
  );
}
